import React, { useState } from 'react';
import { Card, Input, Button } from '@/components/ui';
import { FormItem, Form } from '@/components/ui/Form';
import { triggerSuccessMessage, triggerErrorMessage } from '@/components/ui/toast/notifications';
import { useManageAccount } from '@/api/account';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ZodType } from 'zod';

interface AccountDetailsProps {
  initialName: string;
  email: string;
}

type AccountDetailsFormSchema = {
  name: string;
};

const validationSchema: ZodType<AccountDetailsFormSchema> = z.object({
  name: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().max(255, { message: 'Name must not exceed 255 characters' })),
});

const AccountDetails: React.FC<AccountDetailsProps> = ({ initialName, email }) => {
  const [isSubmitting, setSubmitting] = useState(false);
  const { updateProfile } = useManageAccount();

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    setError,
  } = useForm<AccountDetailsFormSchema>({
    defaultValues: {
      name: initialName,
    },
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (values: AccountDetailsFormSchema) => {
    setSubmitting(true);

    try {
      await updateProfile({ name: values.name });

      // Show success notification
      triggerSuccessMessage('Profile updated successfully');
    } catch (error: any) {
      // Handle validation errors from API
      if (error?.response?.data?.errors?.name) {
        setError('name', {
          type: 'manual',
          message: error.response.data.errors.name[0],
        });
      } else {
        // Show error notification
        triggerErrorMessage(error?.message || 'Failed to update profile');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      header={{
        content: <h2 className="text-lg font-semibold">Account Details</h2>,
        bordered: true,
      }}
    >
      <Form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormItem
          label="Full Name"
          invalid={Boolean(errors.name)}
          errorMessage={errors.name?.message}
        >
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                type="text"
                placeholder="Enter your full name"
                autoComplete="name"
                {...field}
              />
            )}
          />
        </FormItem>

        <FormItem label="Email" className="opacity-75">
          <Input
            type="email"
            value={email}
            disabled
            readOnly
            className="bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Email cannot be changed
          </p>
        </FormItem>

        <div className="flex justify-end pt-2">
          <Button
            variant="solid"
            type="submit"
            disabled={!isDirty}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default AccountDetails;

